import { useTranslation } from 'react-i18next';
import { Check, X } from 'lucide-react';

import { blankSize, tiltWrapCompensation, verticalImbalance } from '../formulas';
import { formatFixed } from '../format';
import { NumField, Note, ResultBox, useNumberField } from '../ui/primitives';
import styles from './cards.module.css';

// Блок 2 «Мастер-оптик» — калькуляторы 10–12.

export function BlankSizeCalc(): JSX.Element {
  const { t } = useTranslation();
  const a = useNumberField('53');
  const dbl = useNumberField('17');
  const pd = useNumberField('63');
  const margin = useNumberField('2');

  const result = blankSize({ a: a.num, dbl: dbl.num, pd: pd.num, margin: margin.num });

  return (
    <div>
      <NumField label={t('calculators.blankSize.a')} field={a} step="1" />
      <NumField label={t('calculators.blankSize.dbl')} field={dbl} step="1" />
      <NumField label={t('calculators.blankSize.pd')} field={pd} step="1" />
      <NumField label={t('calculators.blankSize.margin')} field={margin} step="0.5" />
      <ResultBox>
        <div>{t('calculators.blankSize.result', { value: formatFixed(result.mbs, 1) })}</div>
        <div className={styles.blanks}>
          {result.blanks.map((blank) => (
            <span
              key={blank.size}
              className={styles.blank}
              title={t(
                blank.fits ? 'calculators.blankSize.fits' : 'calculators.blankSize.notFits',
                {
                  size: blank.size,
                },
              )}
            >
              {blank.fits ? (
                <Check size={12} aria-hidden="true" className={styles.iconOk} />
              ) : (
                <X size={12} aria-hidden="true" className={styles.iconBad} />
              )}
              Ø{blank.size}
            </span>
          ))}
        </div>
      </ResultBox>
      <Note>{t('calculators.blankSize.note')}</Note>
    </div>
  );
}

export function TiltWrapCalc(): JSX.Element {
  const { t } = useTranslation();
  const sphere = useNumberField('-3');
  const cyl = useNumberField('-1');
  const axis = useNumberField('90');
  const panto = useNumberField('8');
  const wrap = useNumberField('5');

  const result = tiltWrapCompensation({
    rx: { sphere: sphere.num, cyl: cyl.num, axis: axis.num },
    pantoDeg: panto.num,
    wrapDeg: wrap.num,
  });

  return (
    <div>
      <NumField label={t('calculators.tiltWrap.sphere')} field={sphere} step="0.25" />
      <NumField label={t('calculators.tiltWrap.cyl')} field={cyl} step="0.25" />
      <NumField label={t('calculators.tiltWrap.axis')} field={axis} step="1" />
      <NumField label={t('calculators.tiltWrap.panto')} field={panto} step="1" />
      <NumField label={t('calculators.tiltWrap.wrap')} field={wrap} step="1" />
      <ResultBox>
        {t('calculators.tiltWrap.result', {
          sphere: formatFixed(result.sphere),
          cyl: formatFixed(result.cyl),
          axis: result.axis,
        })}
      </ResultBox>
      <Note>{t('calculators.tiltWrap.note')}</Note>
    </div>
  );
}

export function VerticalImbalanceCalc(): JSX.Element {
  const { t } = useTranslation();
  const od = useNumberField('-4');
  const os = useNumberField('-1');
  const drop = useNumberField('10');

  const result = verticalImbalance({
    odPower: od.num,
    osPower: os.num,
    readingDrop: drop.num,
  });
  const verdictKey = `calculators.imbalance.verdict${
    result.verdict.charAt(0).toUpperCase() + result.verdict.slice(1)
  }`;

  return (
    <div>
      <NumField label={t('calculators.imbalance.od')} field={od} step="0.25" />
      <NumField label={t('calculators.imbalance.os')} field={os} step="0.25" />
      <NumField label={t('calculators.imbalance.drop')} field={drop} step="1" />
      <ResultBox>
        {t('calculators.imbalance.result', {
          value: formatFixed(result.difference),
          verdict: t(verdictKey),
        })}
      </ResultBox>
      <Note>{t('calculators.imbalance.note')}</Note>
    </div>
  );
}
